/**
 * AuthLayout — split-screen login shell
 *
 * Left  (380 px fixed) : logo + tagline, login form (Outlet), footer + theme toggle
 * Right (flex-1)       : platform pitch — "What is Nabhos", 9-step loop, diagram, vertical tiles
 *
 * Clicking a vertical tile fires onVerticalClick(sectorId) which LoginPage uses
 * to open the deep-dive popup pre-scrolled to that vertical's section.
 */
import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';

// ─── Context — passes tile-click handler from AuthLayout → LoginPage (via Outlet) ─

import { createContext, useContext } from 'react';

interface AuthLayoutContextValue {
  onVerticalClick: (sectorId: string) => void;
  registerVerticalClickHandler: (fn: (sectorId: string) => void) => void;
}
export const AuthLayoutContext = createContext<AuthLayoutContextValue>({
  onVerticalClick: () => {},
  registerVerticalClickHandler: () => {},
});
export const useAuthLayoutContext = () => useContext(AuthLayoutContext);

// ─── Types ────────────────────────────────────────────────────────────────────

interface Sector {
  id: string;
  title: string;
  subtitle: string;
  tagline: string;
  accentText: string;   // Tailwind text colour class
  accentBorder: string; // Tailwind border colour class when active
  accentBg: string;     // Tailwind bg colour class when active
  badge?: string;
  svg: React.ReactNode;
}

// ─── Sector data ──────────────────────────────────────────────────────────────

const SECTORS: Sector[] = [
  {
    id: 'agrotech',
    title: 'AgroTech Intelligence',
    subtitle: 'Estate Health, Crop Monitoring & Remediation',
    tagline: 'Stop staring at thousands of photos. Talk to your data.',
    accentText: 'text-green-700 dark:text-green-400',
    accentBorder: 'border-green-400 dark:border-green-500',
    accentBg: 'bg-green-50 dark:bg-green-900/20',
    svg: (
      <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3"
           strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 flex-shrink-0">
        <path d="M50 85C50 85 20 60 20 35C20 20 35 15 50 15C65 15 80 20 80 35C80 60 50 85 50 85Z" />
        <path d="M50 85L50 15" strokeDasharray="4 3" />
        <circle cx="50" cy="20" r="3" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'precision',
    title: 'Precision Planning',
    subtitle: 'Advanced AgroTech Add-on · Variable-Rate & Yield Forecasting',
    tagline: 'Cut chemical waste by 30% with AI-generated prescription maps your sprayer reads directly.',
    accentText: 'text-purple-600 dark:text-purple-400',
    accentBorder: 'border-purple-400 dark:border-purple-500',
    accentBg: 'bg-purple-50 dark:bg-purple-900/20',
    badge: 'AgroTech Add-on',
    svg: (
      <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3"
           strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 flex-shrink-0">
        <circle cx="50" cy="50" r="35" />
        <circle cx="50" cy="50" r="18" />
        <line x1="50" y1="15" x2="50" y2="30" />
        <line x1="50" y1="70" x2="50" y2="85" />
        <line x1="15" y1="50" x2="30" y2="50" />
        <line x1="70" y1="50" x2="85" y2="50" />
        <circle cx="50" cy="50" r="5" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'oilgas',
    title: 'Oil & Gas Intelligence',
    subtitle: 'Pipeline Integrity, Upstream Exploration & ERP Integration',
    tagline: 'Detect a hydrocarbon leak through vegetation die-off — weeks before it surfaces.',
    accentText: 'text-blue-600 dark:text-blue-400',
    accentBorder: 'border-blue-400 dark:border-blue-500',
    accentBg: 'bg-blue-50 dark:bg-blue-900/20',
    svg: (
      <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3"
           strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 flex-shrink-0">
        <path d="M10 65L90 65" strokeWidth="4" />
        <rect x="38" y="25" width="24" height="30" rx="2" />
        <circle cx="50" cy="38" r="7" />
        <path d="M75 50C70 40 80 30 75 18" strokeDasharray="3 3" />
      </svg>
    ),
  },
  {
    id: 'infrastructure',
    title: 'Infrastructure & Construction',
    subtitle: 'Site Volumetrics & Structural Wear',
    tagline: 'Know about the unauthorised earthwork at Km 47.2 before your field team does.',
    accentText: 'text-amber-600 dark:text-amber-400',
    accentBorder: 'border-amber-400 dark:border-amber-500',
    accentBg: 'bg-amber-50 dark:bg-amber-900/20',
    svg: (
      <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3"
           strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 flex-shrink-0">
        <path d="M10 70L90 70" strokeWidth="3" />
        <path d="M20 70L20 40L35 30L65 30L80 40L80 70" />
      </svg>
    ),
  },
  {
    id: 'linearassets',
    title: 'Linear Assets Intelligence',
    subtitle: 'Power Lines, Railways & Right-of-Way Clearance',
    tagline: 'Predict vegetation contact with power lines & track obstructions in <6h.',
    accentText: 'text-yellow-600 dark:text-yellow-400',
    accentBorder: 'border-yellow-400 dark:border-yellow-500',
    accentBg: 'bg-yellow-50 dark:bg-yellow-900/20',
    svg: (
      <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3"
           strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 flex-shrink-0">
        <path d="M20 80L80 20" strokeWidth="3" />
        <circle cx="20" cy="80" r="5" fill="currentColor" />
        <circle cx="80" cy="20" r="5" fill="currentColor" />
        <path d="M40 30L60 70" strokeWidth="2" strokeDasharray="3 3" />
        <path d="M50 20L50 80" strokeWidth="2" />
      </svg>
    ),
  },
  {
    id: 'geotech',
    title: 'Environmental Geotech',
    subtitle: 'Protected Zone Monitoring, Carbon Verification & Geohazard Risk',
    tagline: 'Prove deforestation happened — with timestamped, tamper-proof satellite evidence.',
    accentText: 'text-teal-600 dark:text-teal-400',
    accentBorder: 'border-teal-400 dark:border-teal-500',
    accentBg: 'bg-teal-50 dark:bg-teal-900/20',
    svg: (
      <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="3"
           strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 flex-shrink-0">
        <path d="M50 85C30 65 15 50 15 35C15 20 30 12 50 12C70 12 85 20 85 35C85 50 70 65 50 85Z" />
        <path d="M50 85L50 30" strokeDasharray="4 3" />
        <path d="M35 42C40 38 45 36 50 36C55 36 60 38 65 42" />
        <circle cx="50" cy="24" r="4" fill="currentColor" />
      </svg>
    ),
  },
];

// ─── 9-step loop data ─────────────────────────────────────────────────────────

const LOOP_STEPS = [
  { n: '1', label: 'Define Area',         sub: 'Boundary + metadata' },
  { n: '2', label: 'Satellite Survey',    sub: 'Sentinel-2 / Landsat' },
  { n: '3', label: 'AI Analysis',         sub: 'Anomaly + narrative' },
  { n: '4', label: 'Human Gate 1',        sub: 'Confirm / reject / defer' },
  { n: '5', label: 'Drone Plan',          sub: 'AI payload + flight path' },
  { n: '6', label: 'Fly + Upload',        sub: 'Telemetry ingestion' },
  { n: '7', label: 'Human Gate 2',        sub: 'Verify drone findings' },
  { n: '8', label: 'Respond',             sub: 'Remediation / enforcement' },
  { n: '9', label: 'Ask Nabhos',          sub: 'NLQ across all history' },
];

// ─── Right Panel ──────────────────────────────────────────────────────────────

function RightPanel({ onVerticalClick }: { onVerticalClick: (id: string) => void }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <div
      className="w-full flex flex-col px-8 pt-8 pb-12 space-y-7 bg-white dark:bg-gray-900"
      style={{
        backgroundImage: 'radial-gradient(rgba(128,128,128,0.12) 1px, transparent 0)',
        backgroundSize: '20px 20px',
      }}
    >

      {/* ── Platform pitch ─────────────────────────────────────────── */}
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
        <strong className="text-gray-800 dark:text-gray-200">Nabhos</strong> is an agentic intelligence platform that bridges free satellite imagery and precision drone telemetry with actionable decisions across industries. Just subscribe for the ones in which your organization operates.
      </p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
        {[
          'One platform.',
          'Five industry verticals.',
          'One conversational AI that knows your entire operational history.',
        ].map((phrase, i, arr) => (
          <>
            <span
              key={phrase}
              className="text-sm font-bold text-gray-900 dark:text-white"
              style={{ fontFamily: "'Architects Daughter', cursive" }}
            >
              {phrase}
            </span>
            {i < arr.length - 1 && (
              <span key={`dot-${i}`} className="text-green-500 dark:text-green-400 font-bold text-base leading-none select-none">·</span>
            )}
          </>
        ))}
      </div>

      {/* ── Vertical tiles ─────────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">
          Industry verticals — click to explore
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {SECTORS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onVerticalClick(s.id)}
              className={`w-full text-left flex items-start gap-3 px-3.5 py-3 rounded-lg border transition-all duration-150 group
                bg-white dark:bg-gray-800/60 border-gray-200 dark:border-gray-700
                hover:${s.accentBorder} hover:${s.accentBg}`}
            >
              <span className={`mt-0.5 ${s.accentText}`}>{s.svg}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{s.title}</p>
                  {s.badge && (
                    <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">
                      {s.badge}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-snug mt-0.5">{s.subtitle}</p>
                <p
                  className="text-[10px] italic text-gray-500 dark:text-gray-400 mt-1 leading-snug"
                  style={{ fontFamily: "'Architects Daughter', cursive" }}
                >
                  {s.tagline}
                </p>
              </div>
              <span className="text-gray-300 dark:text-gray-600 text-xs group-hover:text-gray-500 dark:group-hover:text-gray-400 mt-1 flex-shrink-0">›</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Diagram thumbnail ───────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">
          How it works
        </p>
        <p
          className="text-xs font-semibold text-gray-600 dark:text-gray-300 leading-snug mb-2"
          style={{ fontFamily: "'Architects Daughter', cursive" }}
        >
          Satellite detects anomalies → AI reasons &amp; plans the drone → Drone confirms &amp; diagnoses → Human verifies → AI drives remediation
        </p>
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="relative w-full rounded-lg overflow-visible group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label="View full diagram"
          style={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.09), 0 6px 20px rgba(0,0,0,0.07)',
            border: '1px solid #e5e7eb',
          }}
        >
          {/* Pushpin */}
          <div
            className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full z-10"
            style={{
              background: '#2e7d32',
              boxShadow: 'inset -2px -2px 4px rgba(0,0,0,0.25), inset 2px 2px 4px rgba(255,255,255,0.4), 2px 5px 8px rgba(46,125,50,0.3)',
            }}
          >
            <div className="absolute top-0.5 left-0.5 w-1 h-1 rounded-full bg-white/80" />
          </div>
          <img
            src="/nabhos_stickFig01.jpeg"
            alt="Nabhos AI workflow — click to enlarge"
            className="w-full rounded-lg block"
          />
          <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
              Click to enlarge
            </span>
          </div>
        </button>
      </div>

      {/* ── 9-step loop ────────────────────────────────────────────── */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">
          The Intelligence Loop — universal across all verticals
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {LOOP_STEPS.map((s) => (
            <div
              key={s.n}
              className="flex items-start gap-1.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-md px-2.5 py-2"
            >
              <span
                className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white mt-0.5"
                style={{ background: '#276749' }}
              >
                {s.n}
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-gray-800 dark:text-gray-200 leading-tight truncate">{s.label}</p>
                <p className="text-[9px] text-gray-400 dark:text-gray-500 leading-tight truncate">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 p-6"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-9 right-0 text-white/80 hover:text-white text-sm font-medium flex items-center gap-1"
              aria-label="Close"
            >
              ✕ Close
            </button>
            <img
              src="/nabhos_stickFig01.jpeg"
              alt="Nabhos AI workflow — full size"
              className="w-full rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Nabhos logo ──────────────────────────────────────────────────────────────

function NabhosLogo({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className={className} aria-hidden="true">
      <path d="M 32,32 L 68,68 M 68,32 L 32,68" fill="none" stroke="#64748b" strokeWidth="3" strokeLinecap="round" />
      <circle cx="32" cy="32" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      <circle cx="68" cy="32" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      <circle cx="32" cy="68" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      <circle cx="68" cy="68" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      <path d="M 36,50 Q 50,37 64,50 Q 50,63 36,50 Z" fill="white" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" />
      <circle cx="50" cy="50" r="5.5" fill="#276749" />
      <circle cx="50" cy="50" r="2.5" fill="#0f172a" />
      <circle cx="51.5" cy="48.5" r="1" fill="white" />
    </svg>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function AuthLayout() {
  const { theme, toggleTheme } = useThemeStore();

  // Vertical click handler — registered by LoginPage via context
  const [verticalClickHandler, setVerticalClickHandler] = useState<((id: string) => void) | null>(null);

  const contextValue: AuthLayoutContextValue = {
    onVerticalClick: (id: string) => {
      if (verticalClickHandler) verticalClickHandler(id);
    },
    registerVerticalClickHandler: (fn) => {
      setVerticalClickHandler(() => fn);
    },
  };

  return (
    <AuthLayoutContext.Provider value={contextValue}>
      <div className="min-h-screen flex items-start overflow-x-hidden">

        {/* ── Left: logo, form, footer ── */}
        <div className="w-full md:w-[380px] md:flex-shrink-0 flex flex-col bg-white dark:bg-gray-900 md:border-r border-gray-100 dark:border-gray-800 px-7 pt-7 pb-6">

          {/* Logo + name */}
          <div className="flex items-center gap-4 mb-6">
            <NabhosLogo className="flex-shrink-0 w-24 h-24" />
            <div className="flex flex-col justify-center min-w-0">
              <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white leading-tight">
                Nabhos
              </h1>
              <p
                className="text-sm text-orange-500 font-medium leading-tight mt-0.5"
                style={{ fontFamily: "'Architects Daughter', cursive" }}
              >
                AI eye in the sky
              </p>
            </div>
          </div>

          {/* Auth form */}
          <Outlet />

          {/* Mobile: marketing panel below login form */}
          <div className="md:hidden mt-6">
            <div className="border-t border-gray-100 dark:border-gray-800 -mx-7" />
            <RightPanel onVerticalClick={contextValue.onVerticalClick} />
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-gray-300 dark:text-gray-600">
              © 2026 Nabhos. All rights reserved.
            </p>
            <button
              type="button"
              onClick={toggleTheme}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
            </button>
          </div>
        </div>

        {/* ── Right: desktop only ── */}
        <div className="hidden md:flex md:flex-1 min-w-0">
          <RightPanel onVerticalClick={contextValue.onVerticalClick} />
        </div>

      </div>
    </AuthLayoutContext.Provider>
  );
}

// Made with Bob
